// Desk build only: tells Route Desk what is in the cart, so it can warn an
// officer before switching CHE empties the cart (cart reducer,
// setOrderingForSuccess). Route Desk cannot look inside this frame.
//
// Message (plan 2026-10-12 Piece B change 6), posted to the embedding Route
// Desk page through the same allowlist and targeting as login-ready:
//   { type: "he-desk:cart-status", orderingFor: <CHE odoo id or null>,
//     items: <number of cart lines>, total: <number>, currency: "UGX" or null }
// Nothing else leaves the app: no product lines, names or phone numbers.
//
// Sent once at start, again after login-ready, and whenever the tuple
// changes. Signed out: orderingFor null, items 0, total 0, currency null.

import { postToRouteDesk } from "./deskLoginPhone";
import { checkAuth } from "./requireAuthentication";

export const CART_STATUS_TYPE = "he-desk:cart-status";

export const SIGNED_OUT_CART_STATUS = Object.freeze({
  type: CART_STATUS_TYPE,
  orderingFor: null,
  items: 0,
  total: 0,
  currency: null,
});

// Builds the message from the redux state:
//   orderingFor  cart.ordering_for.odoo_id   (the CHE the cart belongs to;
//                employees only, since only they order for someone)
//   items        cart.order.length           (cart lines; free products not counted)
//   total        cart.total
//   currency     auth.user.country.currency  (e.g. "UGX")
export const cartStatusFromState = (state, signedIn) => {
  const user = state?.auth?.user;
  if (!signedIn || !user) return { ...SIGNED_OUT_CART_STATUS };
  const cart = state?.cart || {};
  const odooId = user.contact_type === "employee" ? Number(cart.ordering_for?.odoo_id) : NaN;
  const total = Number(cart.total);
  const currency = user.country?.currency;
  return {
    type: CART_STATUS_TYPE,
    orderingFor: Number.isInteger(odooId) && odooId > 0 ? odooId : null,
    items: Array.isArray(cart.order) ? cart.order.length : 0,
    total: Number.isFinite(total) ? total : 0,
    currency: typeof currency === "string" && currency ? currency : null,
  };
};

const sameStatus = (a, b) =>
  !!a && !!b && a.orderingFor === b.orderingFor && a.items === b.items &&
  a.total === b.total && a.currency === b.currency;

let resendCurrent = null;

// Called once at app start with the redux store. Outside a frame it does
// nothing. Returns { unsubscribe, resend } (resend posts even if unchanged).
export const startDeskCartStatus = (store, { win = window, isSignedIn = checkAuth } = {}) => {
  if (!win.parent || win.parent === win) return { unsubscribe: () => {}, resend: () => {} };
  let last = null;
  const send = (force) => {
    const status = cartStatusFromState(store.getState(), isSignedIn());
    if (!force && sameStatus(last, status)) return;
    last = status;
    postToRouteDesk(status, win);
  };
  send(true);
  const unsubscribe = store.subscribe(() => send(false));
  const resend = () => send(true);
  resendCurrent = resend;
  return { unsubscribe, resend };
};

// For the Sign In page, right after login-ready: Route Desk may only start
// listening once the frame has loaded, so repeat the latest status there.
export const resendCartStatus = () => {
  if (resendCurrent) resendCurrent();
};
